/**
 * useFocusTodayItems
 * Combines tasks, weaknesses, and study plan activities
 * with intelligent scoring to surface top priority items
 */

import { useMemo } from 'react';
import { useActiveTasks } from './use-tasks';
import { useProfileBeliefs } from './use-learning-profile';
import { useActiveStudyPlan } from './use-study-plan';
import { useSyllabi } from './use-syllabus';
import { differenceInDays, isToday, parseISO, isBefore, addDays } from 'date-fns';
import type { UserTask } from '@/types/task';
import type { ProfileBelief } from '@/types/learning-profile';

// ============================================================================
// TYPES
// ============================================================================

export interface FocusTodayItem {
  id: string;
  type: 'task' | 'diagnostic' | 'weakness' | 'exam_prep' | 'milestone';
  title: string;
  description?: string;
  estimatedMinutes?: number;
  priority: 'low' | 'normal' | 'high' | 'urgent';
  actionUrl: string;
  actionLabel: string;
  source: string;
  dueToday?: boolean;
  taskId?: string; // If this is a task, include ID for complete/dismiss
  score: number;
}

// ============================================================================
// SCORING WEIGHTS
// ============================================================================

const WEIGHTS = {
  // Source type weights
  SOURCE: {
    task: 0,
    diagnostic: 40,
    weakness: 35,
    exam_prep: 30,
    milestone: 25,
  },
  // Priority weights
  PRIORITY: {
    urgent: 100,
    high: 60,
    normal: 30,
    low: 10,
  },
  // Bonuses
  DUE_TODAY: 50,
  CONFIDENCE_MULTIPLIER: 20, // multiplied by confidence 0-1
  DAYS_UNTIL_EXAM_FACTOR: 3, // multiplied by (14 - daysUntil)
};

// ============================================================================
// CONVERSION FUNCTIONS
// ============================================================================

function taskToFocusItem(task: UserTask): FocusTodayItem {
  const dueToday = task.due_date ? isToday(parseISO(task.due_date)) : false;
  const priority = priorityFromNumber(task.priority);

  // Calculate score
  let score = WEIGHTS.PRIORITY[priority] + WEIGHTS.SOURCE.task;
  if (dueToday) score += WEIGHTS.DUE_TODAY;

  // Use metadata action_url for special actions (e.g., action:triggerOnboarding)
  // Otherwise navigate to task detail view
  const metadataUrl = task.metadata?.action_url as string | undefined;
  const isSpecialAction = metadataUrl?.startsWith('action:');
  const actionUrl = isSpecialAction ? metadataUrl : `/dashboard/tasks/view/${task.id}`;
  const actionLabel = isSpecialAction ? 'Start' : 'View';

  return {
    id: `task-${task.id}`,
    type: task.task_type === 'diagnostic' ? 'diagnostic' : 'task',
    title: task.title,
    description: task.description || undefined,
    estimatedMinutes: task.estimated_minutes || undefined,
    priority,
    actionUrl,
    actionLabel,
    source: 'task',
    dueToday,
    taskId: task.id,
    score,
  };
}

function weaknessToFocusItem(belief: ProfileBelief): FocusTodayItem {
  const daysSincePractice = belief.last_practiced_at
    ? differenceInDays(new Date(), parseISO(belief.last_practiced_at))
    : Infinity;

  // Calculate priority based on confidence and neglect
  let priority: FocusTodayItem['priority'] = 'normal';
  if (belief.confidence >= 0.8 || daysSincePractice >= 21) {
    priority = 'high';
  } else if (belief.confidence >= 0.9 || daysSincePractice >= 28) {
    priority = 'urgent';
  }

  // Calculate score
  let score = WEIGHTS.PRIORITY[priority] + WEIGHTS.SOURCE.weakness;
  score += (belief.confidence || 0.5) * WEIGHTS.CONFIDENCE_MULTIPLIER;

  const title = belief.subcategory
    ? `Review ${belief.category} / ${belief.subcategory}`
    : `Review ${belief.category}`;

  const description = daysSincePractice === Infinity
    ? 'Never practiced'
    : `Last practiced ${daysSincePractice} days ago`;

  return {
    id: `weakness-${belief.id}`,
    type: 'weakness',
    title,
    description,
    estimatedMinutes: 15,
    priority,
    actionUrl: getFeatureRouteForBelief(belief),
    actionLabel: 'Practice',
    source: 'performance_insight',
    score,
  };
}

function examToFocusItem(exam: {
  name?: string;
  type?: string;
  date: string;
  topics_covered?: string[];
  course: string;
}): FocusTodayItem {
  const daysUntil = differenceInDays(parseISO(exam.date), new Date());

  // Calculate priority based on proximity
  let priority: FocusTodayItem['priority'] = 'normal';
  if (daysUntil <= 3) {
    priority = 'urgent';
  } else if (daysUntil <= 7) {
    priority = 'high';
  }

  // Calculate score
  let score = WEIGHTS.PRIORITY[priority] + WEIGHTS.SOURCE.exam_prep;
  score += Math.max(0, 14 - daysUntil) * WEIGHTS.DAYS_UNTIL_EXAM_FACTOR;

  const examName = exam.name || exam.type || 'Exam';
  const title = `Prepare for ${exam.course} ${examName}`;
  const daysText = Number.isNaN(daysUntil) ? 'Coming up' : `${daysUntil} days away`;
  const description = `${daysText}${exam.topics_covered?.length ? ` - ${exam.topics_covered.slice(0, 2).join(', ')}` : ''}`;

  return {
    id: `exam-${exam.course}-${exam.date}`,
    type: 'exam_prep',
    title,
    description,
    estimatedMinutes: 60,
    priority,
    actionUrl: `/flashcards/study?subject=${encodeURIComponent(exam.course.toLowerCase())}`,
    actionLabel: 'Study',
    source: 'syllabus',
    score,
  };
}

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

function priorityFromNumber(priority: number): FocusTodayItem['priority'] {
  if (priority >= 3) return 'urgent';
  if (priority >= 2) return 'high';
  if (priority >= 1) return 'normal';
  return 'low';
}

function getFeatureRouteForBelief(belief: ProfileBelief): string {
  // Route based on belief category/subcategory
  const subject = belief.category?.toLowerCase().replace(/\s+/g, '_') || '';

  // Default to flashcards for most weaknesses
  if (subject) {
    return `/flashcards/study?subject=${encodeURIComponent(subject)}`;
  }

  return '/flashcards/study';
}

// ============================================================================
// MAIN HOOK
// ============================================================================

export function useFocusTodayItems(maxItems = 5) {
  const { data: activeTasks = [], isLoading: tasksLoading } = useActiveTasks();
  const { data: weaknesses = [], isLoading: weaknessesLoading } = useProfileBeliefs('weakness');
  const { isLoading: planLoading } = useActiveStudyPlan();
  const { data: syllabi = [], isLoading: syllabiLoading } = useSyllabi();

  const isLoading = tasksLoading || weaknessesLoading || planLoading || syllabiLoading;

  const focusItems = useMemo(() => {
    const items: FocusTodayItem[] = [];

    // 1. Convert active tasks (priority >= high OR due today)
    for (const task of activeTasks) {
      const dueToday = task.due_date ? isToday(parseISO(task.due_date)) : false;
      if (task.priority >= 2 || dueToday) {
        items.push(taskToFocusItem(task));
      }
    }

    // 2. Add top weaknesses (neglected 14+ days, high confidence)
    const neglectedWeaknesses = weaknesses
      .filter((w) => {
        if (!w.last_practiced_at) return true; // Never practiced = show
        const daysSince = differenceInDays(new Date(), parseISO(w.last_practiced_at));
        return daysSince >= 14;
      })
      .filter((w) => (w.confidence || 0) >= 0.5) // Only confident weaknesses
      .slice(0, 3); // Limit to top 3 weaknesses

    for (const weakness of neglectedWeaknesses) {
      items.push(weaknessToFocusItem(weakness));
    }

    // 3. Add upcoming exams from syllabi (within 14 days)
    const now = new Date();
    const twoWeeksFromNow = addDays(now, 14);

    for (const syllabus of syllabi) {
      const exams = syllabus.parsed_data?.exams || [];
      for (const exam of exams) {
        if (!exam.date) continue;
        const examDate = parseISO(exam.date);
        if (isBefore(examDate, now)) continue; // Skip past exams
        if (isBefore(twoWeeksFromNow, examDate)) continue; // Skip exams > 14 days

        items.push(examToFocusItem({
          ...exam,
          date: exam.date, // Asserted non-null by continue check above
          course: syllabus.course_name_extracted || syllabus.file_name || 'Course',
        }));
      }
    }

    // 4. Sort by: due today first, then by priority (descending)
    items.sort((a, b) => {
      // Due today items come first
      if (a.dueToday && !b.dueToday) return -1;
      if (!a.dueToday && b.dueToday) return 1;
      // Within same due-today status, sort by priority
      const priorityOrder = { urgent: 4, high: 3, normal: 2, low: 1 };
      return priorityOrder[b.priority] - priorityOrder[a.priority];
    });

    // Deduplicate by title (in case task and weakness overlap)
    const seen = new Set<string>();
    const deduped = items.filter((item) => {
      const key = item.title.toLowerCase();
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });

    return deduped.slice(0, maxItems);
  }, [activeTasks, weaknesses, syllabi, maxItems]);

  return {
    data: focusItems,
    isLoading,
  };
}

export default useFocusTodayItems;
